// src/consoleSink.ts
var CONSOLE_METHOD = {
  debug: console.debug.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console)
};
var consoleSink = {
  name: "console",
  write(entry) {
    CONSOLE_METHOD[entry.level](
      JSON.stringify(entry, (_key, value) => typeof value === "bigint" ? value.toString() : value)
    );
  }
};

// src/correlation.ts
var CORRELATION_ID_HEADER = "x-correlation-id";
function generateId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
var sessionId = null;
function getSessionId() {
  if (sessionId === null) sessionId = generateId();
  return sessionId;
}
function newRequestId() {
  return generateId();
}
function attachCorrelationId(config) {
  const requestId = newRequestId();
  config.headers.set(CORRELATION_ID_HEADER, requestId);
  return requestId;
}

// src/redact.ts
var REDACTED = "[redacted]";
var MAX_DEPTH = 6;
var SENSITIVE_KEY = /(authorization|password|passwd|secret|token|jwt|api[-_]?key|apikey|card|cvv|cvc|ssn|email|phone)/i;
var ALLOWED_KEYS = /* @__PURE__ */ new Set([
  // logging envelope / correlation
  "correlationId",
  "requestId",
  "sessionId",
  "source",
  "level",
  "ts",
  // http request context
  "method",
  "path",
  "status",
  "statusCode",
  "duration",
  "durationMs",
  // error context
  "message",
  "file",
  "line",
  "column",
  "info",
  // non-PII domain identifiers and operational counters
  "auctionId",
  "itemId",
  "bidId",
  "orgId",
  "eventId",
  "invoiceId",
  "userId",
  "amount",
  "count",
  "total",
  "state",
  "kind",
  "type",
  "code",
  "reason",
  "last4"
]);
function isObjectLike(value) {
  return typeof value === "object" && value !== null;
}
function redactValue(value, depth, seen, keyAllowed) {
  if (depth > MAX_DEPTH) return REDACTED;
  if (Array.isArray(value)) {
    if (seen.has(value)) return REDACTED;
    seen.add(value);
    return value.map((entry) => redactValue(entry, depth + 1, seen, keyAllowed));
  }
  if (isObjectLike(value)) {
    if (seen.has(value)) return REDACTED;
    seen.add(value);
    return redactRecord(value, depth + 1, seen);
  }
  if (!keyAllowed) return REDACTED;
  return typeof value === "string" ? redactString(value) : value;
}
function redactRecord(input, depth, seen) {
  const output = {};
  for (const [key, value] of Object.entries(input)) {
    if (SENSITIVE_KEY.test(key)) {
      output[key] = REDACTED;
      continue;
    }
    const keyAllowed = ALLOWED_KEYS.has(key);
    if (keyAllowed || isObjectLike(value)) {
      output[key] = redactValue(value, depth, seen, keyAllowed);
    } else {
      output[key] = REDACTED;
    }
  }
  return output;
}
function redactSensitive(context) {
  if (!context) return context;
  return redactRecord(context, 0, /* @__PURE__ */ new WeakSet());
}
var FREE_TEXT_PATTERNS = [
  /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g,
  // email addresses
  /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi,
  // bearer tokens
  /\beyJ[A-Za-z0-9._-]{10,}/g,
  // JWT-shaped blobs
  /\b(?:\d[ -]?){13,19}\b/g
  // card-like digit runs
];
function redactString(input) {
  return FREE_TEXT_PATTERNS.reduce((text, pattern) => text.replace(pattern, REDACTED), input);
}

// src/types.ts
var LOG_LEVELS = ["debug", "info", "warn", "error"];

// src/logger.ts
function createLogger({ source, getLogLevel }) {
  let sinks = [consoleSink];
  function registerSink(sink) {
    if (sinks.some((existing) => existing.name === sink.name)) return;
    sinks = [...sinks, sink];
  }
  function resetSinks() {
    sinks = [consoleSink];
  }
  function thresholdIndex() {
    const index = LOG_LEVELS.indexOf(getLogLevel());
    return index >= 0 ? index : LOG_LEVELS.indexOf("info");
  }
  function isEnabled(level) {
    return LOG_LEVELS.indexOf(level) >= thresholdIndex();
  }
  function emit(level, msg, context) {
    if (!isEnabled(level)) return;
    const entry = {
      ts: (/* @__PURE__ */ new Date()).toISOString(),
      level,
      source,
      // msg is free text that can embed inline secrets/PII, so scrub it
      // the same way redactSensitive scrubs allowlisted string context.
      msg: redactString(msg),
      sessionId: getSessionId(),
      context: redactSensitive(context)
    };
    for (const sink of sinks) {
      try {
        sink.write(entry);
      } catch {
      }
    }
  }
  const logger = {
    debug: (msg, context) => emit("debug", msg, context),
    info: (msg, context) => emit("info", msg, context),
    warn: (msg, context) => emit("warn", msg, context),
    error: (msg, context) => emit("error", msg, context)
  };
  return { logger, registerSink, resetSinks };
}

// src/errorCapture.ts
function toError(value) {
  return value instanceof Error ? value : new Error(String(value));
}
function createErrorHandlers({ logger, reportError }) {
  function installGlobalErrorHandlers(app) {
    const onError = (event) => {
      const error = event.error instanceof Error ? event.error : toError(event.message);
      logger.error("uncaught_error", {
        message: error.message,
        file: event.filename,
        line: event.lineno,
        column: event.colno
      });
      reportError(error, "window.onerror", {
        file: event.filename,
        line: event.lineno,
        column: event.colno
      });
    };
    const onRejection = (event) => {
      const error = toError(event.reason);
      logger.error("unhandled_rejection", { message: error.message });
      reportError(error, "unhandledrejection");
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    const previousHandler = app.config.errorHandler;
    app.config.errorHandler = (err, instance, info) => {
      const error = toError(err);
      logger.error("vue_error", { message: error.message, info });
      reportError(error, info);
      if (typeof previousHandler === "function") {
        previousHandler(err, instance, info);
      }
    };
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      app.config.errorHandler = previousHandler;
    };
  }
  return { installGlobalErrorHandlers };
}

export { CORRELATION_ID_HEADER, LOG_LEVELS, attachCorrelationId, consoleSink, createErrorHandlers, createLogger, getSessionId, newRequestId, redactSensitive, redactString };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map